import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Parse from '../api/config';
import { useAuth } from './AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { archivable } from '../hooks/useApprovalQueue';
import { JobApplicationsManager } from './JobApplicationsManager';
import { People } from './admin/People';
import {
    Banner,
    Button,
    Card,
    EmptyState,
    LoadingOverlay,
    Modal,
    PageBody,
    PageHeader,
    StatusBadge,
    cx,
    isApprovalRole,
    isArchived,
    roleLabel,
} from './ui';

const formatDate = (value) => {
    if (!value) return '—';
    const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : format(date, 'MMM d, yyyy');
};

const QUEUES = [
    { key: 'pending', label: 'Pending', fn: 'getPendingFormsForRole' },
    { key: 'approved', label: 'Approved', fn: 'getApprovedFormsByRole' },
    { key: 'denied', label: 'Denied', fn: 'getDeniedFormsByRole' },
    { key: 'cancelled', label: 'Cancelled', fn: 'getCancelledFormsByRole' },
];

const Tabs = ({ tabs, active, onChange }) => (
    <div className="-mb-px flex gap-1 overflow-x-auto">
        {tabs.map((tab) => (
            <button
                key={tab.key}
                type="button"
                onClick={() => onChange(tab.key)}
                className={cx(
                    'whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                    active === tab.key
                        ? 'border-brand-700 text-brand-800'
                        : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-800'
                )}
            >
                {tab.label}
                {typeof tab.count === 'number' && (
                    <span className="ml-1.5 tabular-nums text-neutral-400">{tab.count}</span>
                )}
            </button>
        ))}
    </div>
);

/* --------------------------------------------------------------- approvals */

const RoleApprovals = ({ roles }) => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [activeRole, setActiveRole] = useState(roles[0] || null);
    const [queue, setQueue] = useState('pending');
    const [forms, setForms] = useState({});
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);

    const [denying, setDenying] = useState(null);
    const [reason, setReason] = useState('');

    const load = useCallback(async () => {
        if (!activeRole) return;
        setLoading(true);
        setError(null);
        try {
            const results = await Promise.all(
                QUEUES.map(async (q) => {
                    const result = await Parse.Cloud.run(q.fn, { role: activeRole });
                    // Events that finished weeks ago aren't waiting on anyone.
                    const forms = (result.forms || []).filter((form) => !isArchived(archivable(form)));
                    return [q.key, forms];
                })
            );
            setForms(Object.fromEntries(results));
        } catch (err) {
            console.error('Could not load the approval queues:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [activeRole]);

    useEffect(() => {
        load();
    }, [load]);

    const decide = async (formId, approved, denialReason) => {
        setBusy(approved ? 'Approving…' : 'Denying…');
        setError(null);
        try {
            const result = await Parse.Cloud.run(approved ? 'approveForm' : 'denyForm', {
                formId,
                role: activeRole,
                userEmail: user?.email,
                ...(approved ? {} : { reason: denialReason }),
            });
            if (!result?.success) throw new Error(result?.message || 'That did not go through.');
            setDenying(null);
            setReason('');
            await load();
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setBusy(null);
        }
    };

    const openPdf = async (formId) => {
        setBusy('Generating PDF…');
        try {
            const result = await Parse.Cloud.run('generatePDF', { formId });
            if (!result?.url) throw new Error('The server did not return a PDF.');
            window.open(result.url, '_blank', 'noopener');
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(null);
        }
    };

    const rows = forms[queue] || [];

    const queueTabs = useMemo(
        () => QUEUES.map((q) => ({ ...q, count: (forms[q.key] || []).length })),
        [forms]
    );

    if (!roles.length) {
        return (
            <Card>
                <EmptyState
                    title="You don't hold an approver role"
                    description="Approval roles are granted under People. Anyone with a role sees their queue here."
                />
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            {busy && <LoadingOverlay message={busy} />}
            {error && (
                <Banner tone="danger" title="Something went wrong">
                    {error}
                </Banner>
            )}

            {roles.length > 1 && (
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium uppercase tracking-wider text-neutral-400">Acting as</span>
                    {roles.map((role) => (
                        <button
                            key={role}
                            type="button"
                            onClick={() => setActiveRole(role)}
                            className={cx(
                                'rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors',
                                activeRole === role
                                    ? 'bg-brand-700 text-white ring-brand-700'
                                    : 'bg-white text-neutral-600 ring-neutral-300 hover:bg-neutral-50'
                            )}
                        >
                            {roleLabel(role)}
                        </button>
                    ))}
                </div>
            )}

            <div className="border-b border-neutral-200">
                <Tabs tabs={queueTabs} active={queue} onChange={setQueue} />
            </div>

            <Card className="overflow-hidden">
                {loading ? (
                    <EmptyState title="Loading…" />
                ) : !rows.length ? (
                    <EmptyState title={`Nothing ${queue}`} description={`No requests are ${queue} for ${roleLabel(activeRole)}.`} />
                ) : (
                    <table className="min-w-full divide-y divide-neutral-200">
                        <thead className="bg-neutral-50">
                            <tr className="text-left text-xxs uppercase tracking-wider text-neutral-500">
                                <th scope="col" className="px-5 py-2.5 font-semibold">Event</th>
                                <th scope="col" className="hidden px-5 py-2.5 font-semibold md:table-cell">Requested by</th>
                                <th scope="col" className="px-5 py-2.5 font-semibold">
                                    {queue === 'denied' ? 'Reason' : queue === 'cancelled' ? 'Reason' : 'Status'}
                                </th>
                                <th scope="col" className="px-5 py-2.5">
                                    <span className="sr-only">Actions</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100">
                            {rows.map((form) => (
                                <tr key={form.id} className="align-top hover:bg-neutral-50/70">
                                    <td className="px-5 py-4">
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/home/form/${form.id}`)}
                                            className="text-left text-sm font-medium text-neutral-900 hover:text-brand-800"
                                        >
                                            {form.eventName || 'Untitled event'}
                                        </button>
                                        <p className="mt-0.5 text-xs text-neutral-500">
                                            {formatDate(form.date)}
                                            {form.location ? ` · ${form.location}` : ''}
                                            {form.department ? ` · ${form.department}` : ''}
                                        </p>
                                    </td>

                                    <td className="hidden px-5 py-4 text-sm text-neutral-600 md:table-cell">
                                        {form.owner?.email || form.owner || '—'}
                                    </td>

                                    <td className="px-5 py-4">
                                        {queue === 'pending' && <StatusBadge status="Submitted" />}
                                        {queue === 'approved' && (
                                            <StatusBadge status={form.isFullyApproved ? 'Fully Approved' : 'Partially Approved'} />
                                        )}
                                        {queue === 'denied' && (
                                            <p className="max-w-xs text-xs text-red-700">
                                                {form.denialReason || 'No reason given'}
                                            </p>
                                        )}
                                        {queue === 'cancelled' && (
                                            <p className="max-w-xs text-xs text-neutral-500">
                                                {form.cancellationReason || 'No reason given'}
                                            </p>
                                        )}
                                    </td>

                                    <td className="px-5 py-4">
                                        <div className="flex items-center justify-end gap-1.5">
                                            {queue === 'pending' ? (
                                                <>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => navigate(`/home/form/${form.id}`)}
                                                    >
                                                        Review
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="danger"
                                                        onClick={() => {
                                                            setDenying(form);
                                                            setReason('');
                                                        }}
                                                    >
                                                        Deny
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="primary"
                                                        onClick={() => decide(form.id, true)}
                                                    >
                                                        Approve
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button
                                                        size="sm"
                                                        variant="secondary"
                                                        onClick={() => navigate(`/home/form/${form.id}`)}
                                                    >
                                                        Open
                                                    </Button>
                                                    {queue === 'approved' && (
                                                        <Button size="sm" variant="ghost" onClick={() => openPdf(form.id)}>
                                                            PDF
                                                        </Button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            <Modal
                open={!!denying}
                onClose={() => setDenying(null)}
                title="Deny this request"
                description={denying?.eventName}
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setDenying(null)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            disabled={!reason.trim()}
                            onClick={() => decide(denying.id, false, reason.trim())}
                        >
                            Deny request
                        </Button>
                    </>
                }
            >
                <label htmlFor="denialReason" className="block text-sm font-medium text-neutral-800">
                    Why are you denying it?
                </label>
                <p className="mt-0.5 text-xs text-neutral-500">
                    The requester sees this, and it's what they'll act on before resubmitting.
                </p>
                <textarea
                    id="denialReason"
                    rows="4"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="The sanctuary is already booked that evening."
                    className="mt-2"
                />
            </Modal>
        </div>
    );
};

/* ------------------------------------------------------------------- page */

export const Administrator = () => {
    const { user } = useAuth();
    const { isAdmin, hasSession, serverEmail, loading: permissionsLoading } = usePermissions();

    const [roles, setRoles] = useState([]);
    const [rolesLoading, setRolesLoading] = useState(true);
    const [tab, setTab] = useState('approvals');

    useEffect(() => {
        if (!user?.email) return;
        (async () => {
            try {
                const result = await Parse.Cloud.run('getUserRoles', { email: user.email });
                // Finance / HR are access roles, not event approvers — keep them
                // out of the approvals screen so they don't show an empty queue.
                setRoles((result.roles || []).filter(isApprovalRole));
            } catch (error) {
                console.error('Could not load your roles:', error);
            } finally {
                setRolesLoading(false);
            }
        })();
    }, [user]);

    const tabs = [
        { key: 'approvals', label: 'Approvals' },
        ...(isAdmin ? [{ key: 'people', label: 'People' }] : []),
        { key: 'applications', label: 'Job applications' },
    ];

    if (rolesLoading || permissionsLoading) {
        return (
            <>
                <PageHeader title="Administrator" />
                <PageBody>
                    <Card>
                        <EmptyState title="Loading…" />
                    </Card>
                </PageBody>
            </>
        );
    }

    return (
        <>
            <PageHeader
                title="Administrator"
                description="Approve requests for the roles you hold, and manage who can use this system."
            >
                <div className="mx-auto max-w-6xl px-4 pb-3 sm:px-6 lg:px-8">
                    <Tabs tabs={tabs} active={tab} onChange={setTab} />
                </div>
            </PageHeader>

            <PageBody className="space-y-4">
                {/* A missing Parse session breaks every role-aware call. Say so,
                    rather than quietly showing empty queues and no People tab. */}
                {!hasSession && (
                    <Banner tone="danger" title="The server doesn't recognise your session">
                        You're signed in as {user?.email} in this browser, but the server sees no session, so
                        approvals and administration can't work. Sign out and sign in again.
                    </Banner>
                )}

                {hasSession && !serverEmail && (
                    <Banner tone="warning" title="Your account has no email address on the server">
                        Approvals and roles are keyed to your email, and the server doesn't have one for your
                        account. Sign out and sign in again to attach it.
                    </Banner>
                )}

                {tab === 'approvals' && <RoleApprovals roles={roles} />}
                {tab === 'people' && isAdmin && <People />}
                {tab === 'applications' && <JobApplicationsManager />}
            </PageBody>
        </>
    );
};
